import {
  CalculationsDataExt,
  FieldDataExt,
  InitialRenderContextExt,
  LocalTimeExt,
} from '@castoredc/castor_prose';
import { resolveScheduleName } from '@hub/common';
import { protocolSourceOf } from '@hub/prose-utils';
import { ApplicationError } from 'features/error-handling/ApplicationError';
import { logger } from 'features/logger/logger';
import {
  addPerformanceEndMark,
  addPerformanceStartMark,
} from 'features/monitoring/performanceMonitor';
import { getFieldDefinitions } from './proseFieldOrdering';
import { getProsePanicked } from './prosePanicState';
import {
  getProtocolService,
  getProtocolEditorService,
} from './proseServiceFactory';
import type {
  FieldData,
  Field,
  ParticipantScheduleResponse,
  QuestionnaireDisplay,
  QuestionnairePresentation,
  QuestionnaireStructure,
  RenderResponse,
  StartRenderResponse,
  Section,
  ParticipantScheduleRequestQuery,
  EditWithPreviewError,
  EditWithPreviewResponse,
  StartEditWithPreviewResponse,
  RenderContext,
  ParticipantState,
  CreateParticipantStateResponse,
  ExternalEventFiring,
} from '@castoredc/castor_prose';

import type { DbCalculationsData } from '@hub/common';

function throwIfPanicked(): void {
  if (getProsePanicked()) {
    throw new ApplicationError(
      'PHW-P-003',
      'PROSE module corrupted after panic'
    );
  }
}

type ParticipantStateProvider = {
  create_participant_state: () => CreateParticipantStateResponse;
};

// `render` and `edit_with_preview` require a participant state carrying the
// recorded external-event firings. Questionnaire rendering does not depend on
// firings, so an empty state is the correct input here.
const createParticipantState = (
  service: ParticipantStateProvider
): ParticipantState => {
  const { state } = service.create_participant_state();
  if (!state) {
    throw Error(
      '[Assert failed] PROSE create_participant_state returned no state. This indicates a bug in PROSE.'
    );
  }
  return state;
};

export type ProseRenderInput = {
  dslSource: string;
  questionnaireName: string;
  fieldsData: FieldData;
  contextData: RenderContext;
};

export type ProseCompletionAction = {
  kind: 'redirect';
  destination: string;
};

type ProseCompletedOutput = {
  contextData: RenderContext;
  fieldsData: FieldData;
  calculationsData: DbCalculationsData;
  completionAction: ProseCompletionAction | undefined;
};

export type ProsePostQuestionnaireOutput = ProseRenderOutput;

export type ProseSubmissionRenderResult =
  | { type: 'questionnaire'; output: ProseRenderOutput }
  | { type: 'completed'; output: ProseCompletedOutput }
  | { type: 'postQuestionnaire'; output: ProsePostQuestionnaireOutput };

export type ProseRenderResult =
  | { type: 'questionnaire'; output: ProseRenderOutput }
  | { type: 'postQuestionnaire'; output: ProsePostQuestionnaireOutput }
  // PROSE emits `submitted` when the persisted context already encodes the
  // submission transition (an interrupted finish left the end-context on the
  // doc). It carries no view to render — callers act on the signal itself.
  | { type: 'submitted' };

export type QuestionnaireI18nKeys = {
  i18nHeaderKey: string;
  i18nDescriptionKey: string;
  i18nFooterKey: string;
};

export type ProseRenderOutput = {
  presentation: QuestionnairePresentation | undefined;
  i18nBaseKey: string;
  i18nKeys: QuestionnaireI18nKeys;
  // TODO: [HUB-1577] Enhance Field type safety
  fieldsDefinitions: Field[];
  fieldsData: FieldData;
  fieldsSections: Section[];
  contextData: RenderContext;
  calculationsData: DbCalculationsData;
};

export type ProseStartRenderInput = {
  dslSource: string;
  questionnaireName: string;
  timezone: string;
};

export type ProseEditorRenderInput = Omit<
  ProseRenderInput,
  'questionnaireName'
> & {
  questionnaireName: string | undefined;
};

export type ProseStartEditorRenderInput = {
  dslSource: string;
  questionnaireName: string | undefined;
  timezone: string;
};

type ProseEditorRenderOutput = Omit<ProseRenderOutput, 'contextData'> & {
  contextData: RenderContext | null;
  renderError?: EditWithPreviewError;
};

// PROSE wraps the rendered structure in a QuestionnaireDisplay that also
// carries the participant's navigation location; the client manages paging
// itself, so only the structure is consumed here.
const getDisplayStructure = (
  display: QuestionnaireDisplay
): QuestionnaireStructure => {
  if (!display.structure) {
    throw Error(
      '[Assert failed] PROSE returned a QuestionnaireDisplay without a structure. This indicates a bug in PROSE.'
    );
  }
  return display.structure;
};

const extractQuestionnaireOutput = (
  questionnaire: QuestionnaireStructure,
  fieldsData: FieldData,
  contextData: RenderContext,
  calculationsData: DbCalculationsData
): ProseRenderOutput => {
  const fieldsDefinitions = getFieldDefinitions(questionnaire);

  return {
    i18nBaseKey: questionnaire.i18nBaseKey,
    i18nKeys: {
      i18nHeaderKey: questionnaire.i18nHeaderKey,
      i18nDescriptionKey: questionnaire.i18nDescriptionKey,
      i18nFooterKey: questionnaire.i18nFooterKey,
    },
    presentation: questionnaire.presentation,
    fieldsDefinitions,
    fieldsData,
    fieldsSections: questionnaire.sections,
    contextData,
    calculationsData,
  };
};

export const callProseRender = async (
  input: ProseRenderInput
): Promise<ProseRenderResult> => {
  throwIfPanicked();
  logger.debug('PROSE protocolService.render input', { details: input });

  let response: RenderResponse;
  try {
    addPerformanceStartMark('proseRenderWasmCall');
    const protocolService = await getProtocolService();
    response = protocolService.render({
      protocolSource: protocolSourceOf(input.dslSource),
      questionnaireName: input.questionnaireName,
      fieldData: input.fieldsData,
      context: input.contextData,
      participantState: createParticipantState(protocolService),
    });
  } catch (error) {
    throw new ApplicationError(
      'PHW-P-001',
      error instanceof Error ? error.message : 'Unknown PROSE render error'
    );
  } finally {
    addPerformanceEndMark('proseRenderWasmCall', { console: true });
  }

  let proseOutput: ProseRenderResult;
  try {
    addPerformanceStartMark('proseRenderResponseProcessing');

    const rendered = response.renderedQuestionnaire;
    const output = rendered?.renderOutcome;

    if (!rendered?.context) {
      throw Error(
        '[Assert failed] PROSE render returned no context. This indicates a bug in PROSE.'
      );
    }

    const fieldsData = rendered.fieldData ?? FieldDataExt.new();
    const contextData = rendered.context;
    const calculationsData = rendered.calculationsData;

    if (output?.output?.$case === 'questionnaire') {
      const questionnaire = getDisplayStructure(output.output.value);
      proseOutput = {
        type: 'questionnaire',
        output: extractQuestionnaireOutput(
          questionnaire,
          fieldsData,
          contextData,
          calculationsData
        ),
      };
    } else if (output?.output?.$case === 'postQuestionnaire') {
      const postQuestionnaire = getDisplayStructure(output.output.value);
      proseOutput = {
        type: 'postQuestionnaire',
        output: extractQuestionnaireOutput(
          postQuestionnaire,
          fieldsData,
          contextData,
          calculationsData
        ),
      };
    } else if (output?.output?.$case === 'submitted') {
      // The persisted context re-emits the submission transition — carry the
      // signal without any output; the caller resumes the interrupted submit.
      proseOutput = { type: 'submitted' };
    } else {
      throw Error(
        '[Assert failed] Unable to process PROSE response, neither questionnaire nor postQuestionnaire is defined in structure.'
      );
    }

    logger.debug('PROSE protocolService.render output', {
      details: proseOutput,
    });
  } finally {
    addPerformanceEndMark('proseRenderResponseProcessing', {
      console: true,
    });
  }

  return proseOutput;
};

const renderSubmission = async (
  input: ProseRenderInput
): Promise<RenderResponse> => {
  try {
    addPerformanceStartMark('proseSubmissionRenderWasmCall');
    const protocolService = await getProtocolService();
    return protocolService.render({
      protocolSource: protocolSourceOf(input.dslSource),
      questionnaireName: input.questionnaireName,
      fieldData: input.fieldsData,
      context: input.contextData,
      participantState: createParticipantState(protocolService),
    });
  } catch (error) {
    throw new ApplicationError(
      'PHW-P-001',
      error instanceof Error ? error.message : 'Unknown PROSE render error'
    );
  } finally {
    addPerformanceEndMark('proseSubmissionRenderWasmCall', { console: true });
  }
};

export const callProseSubmissionRender = async (
  input: ProseRenderInput
): Promise<ProseSubmissionRenderResult> => {
  throwIfPanicked();
  logger.debug('PROSE protocolService.render (submission) input', {
    details: input,
  });

  let response = await renderSubmission(input);

  // PROSE emits `submitted` exactly once, on the render that transitions the
  // questionnaire into the submitted state. It carries the updated participant
  // state (not consumed here), not the resulting view, so re-render with the
  // returned submitted context to advance to `completed` or `postQuestionnaire`.
  if (
    response.renderedQuestionnaire?.renderOutcome?.output?.$case === 'submitted'
  ) {
    const submittedContext = response.renderedQuestionnaire.context;
    if (!submittedContext) {
      throw Error(
        '[Assert failed] PROSE submission render returned no context. This indicates a bug in PROSE.'
      );
    }
    response = await renderSubmission({
      ...input,
      contextData: submittedContext,
    });
  }

  let submissionOutput: ProseSubmissionRenderResult;
  try {
    addPerformanceStartMark('proseSubmissionRenderResponseProcessing');

    const rendered = response.renderedQuestionnaire;
    const output = rendered?.renderOutcome;

    if (!rendered?.context) {
      throw Error(
        '[Assert failed] PROSE submission render returned no context. This indicates a bug in PROSE.'
      );
    }

    const contextData = rendered.context;
    const fieldsData = rendered.fieldData ?? FieldDataExt.new();
    const calculationsData = rendered.calculationsData;

    if (output?.output?.$case === 'completed') {
      const completed = output.output.value;
      let completionAction: ProseCompletionAction | undefined;
      if (completed.action?.kind?.$case === 'redirect') {
        completionAction = {
          kind: 'redirect',
          destination: completed.action.kind.value.destination,
        };
      }

      submissionOutput = {
        type: 'completed',
        output: { contextData, fieldsData, calculationsData, completionAction },
      };
    } else if (output?.output?.$case === 'questionnaire') {
      const questionnaire = getDisplayStructure(output.output.value);

      submissionOutput = {
        type: 'questionnaire',
        output: extractQuestionnaireOutput(
          questionnaire,
          fieldsData,
          contextData,
          calculationsData
        ),
      };
    } else if (output?.output?.$case === 'postQuestionnaire') {
      const postQuestionnaire = getDisplayStructure(output.output.value);

      submissionOutput = {
        type: 'postQuestionnaire',
        output: extractQuestionnaireOutput(
          postQuestionnaire,
          fieldsData,
          contextData,
          calculationsData
        ),
      };
    } else {
      throw Error(
        '[Assert failed] Unable to process PROSE submission response, neither questionnaire, completed, nor postQuestionnaire is defined in structure.'
      );
    }

    logger.debug('PROSE protocolService.render (submission) output', {
      details: submissionOutput,
    });
  } finally {
    addPerformanceEndMark('proseSubmissionRenderResponseProcessing', {
      console: true,
    });
  }

  return submissionOutput;
};

export const callProseStartRender = async (
  input: ProseStartRenderInput
): Promise<ProseRenderOutput> => {
  throwIfPanicked();
  logger.debug('PROSE protocolService.start_render input', { details: input });

  let response: StartRenderResponse;
  try {
    addPerformanceStartMark('proseStartRenderWasmCall');
    const protocolService = await getProtocolService();
    const initialContext = InitialRenderContextExt.new(
      LocalTimeExt.now(input.timezone)
    );
    response = protocolService.start_render({
      protocolSource: protocolSourceOf(input.dslSource),
      questionnaireName: input.questionnaireName,
      context: initialContext,
    });
  } catch (error) {
    throw new ApplicationError(
      'PHW-P-001',
      error instanceof Error ? error.message : 'Unknown PROSE render error'
    );
  } finally {
    addPerformanceEndMark('proseStartRenderWasmCall', { console: true });
  }

  let proseOutput: ProseRenderOutput;
  try {
    addPerformanceStartMark('proseStartRenderResponseProcessing');

    const rendered = response.renderedQuestionnaire;
    const output = rendered?.renderOutcome;

    if (output?.output?.$case !== 'questionnaire') {
      throw Error(
        '[Assert failed] Unable to process PROSE response, questionnaire is not defined in structure.'
      );
    }

    const questionnaire = getDisplayStructure(output.output.value);

    if (!rendered?.context) {
      throw Error(
        '[Assert failed] PROSE start_render returned no context. This indicates a bug in PROSE.'
      );
    }

    const fieldsData = rendered.fieldData ?? FieldDataExt.new();
    const contextData = rendered.context;
    const calculationsData = rendered.calculationsData;

    const fieldsDefinitions = getFieldDefinitions(questionnaire);

    proseOutput = {
      i18nBaseKey: questionnaire.i18nBaseKey,
      i18nKeys: {
        i18nHeaderKey: questionnaire.i18nHeaderKey,
        i18nDescriptionKey: questionnaire.i18nDescriptionKey,
        i18nFooterKey: questionnaire.i18nFooterKey,
      },
      presentation: questionnaire.presentation,
      fieldsDefinitions,
      fieldsData,
      fieldsSections: questionnaire.sections,
      contextData,
      calculationsData,
    };

    logger.debug('PROSE protocolService.start_render output', {
      details: proseOutput,
    });
  } finally {
    addPerformanceEndMark('proseStartRenderResponseProcessing', {
      console: true,
    });
  }

  return proseOutput;
};

export const callProseEditWithPreview = async (
  input: ProseEditorRenderInput
): Promise<ProseEditorRenderOutput> => {
  throwIfPanicked();
  logger.debug('PROSE protocolEditorService.edit_with_preview input', {
    details: input,
  });

  let response: EditWithPreviewResponse;

  try {
    addPerformanceStartMark('proseEditWithPreviewWasmCall');
    const protocolEditorService = await getProtocolEditorService();
    response = protocolEditorService.edit_with_preview({
      protocolSource: protocolSourceOf(input.dslSource),
      questionnaireName: input.questionnaireName,
      fieldData: input.fieldsData,
      context: input.contextData,
      participantState: createParticipantState(protocolEditorService),
    });
  } catch (error) {
    logger.debug(
      'PROSE protocolEditorService.edit_with_preview error (used for editor)',
      {
        details:
          error instanceof Error
            ? { message: error.message }
            : { error: String(error) },
      }
    );

    throw error;
  } finally {
    addPerformanceEndMark('proseEditWithPreviewWasmCall', { console: true });
  }

  try {
    addPerformanceStartMark('proseEditWithPreviewResponseProcessing');

    let proseOutput: ProseEditorRenderOutput;

    if (response.error.length > 0) {
      const firstError = response.error[0];

      proseOutput = {
        i18nBaseKey: '',
        i18nKeys: {
          i18nHeaderKey: '',
          i18nDescriptionKey: '',
          i18nFooterKey: '',
        },
        presentation: undefined,
        fieldsDefinitions: [],
        fieldsSections: [],
        fieldsData: FieldDataExt.new(),
        calculationsData: CalculationsDataExt.new(),
        contextData: null,
        renderError: firstError ?? {
          i18nKey: 'errors.prose.render_failed',
          values: {
            message: 'Unknown PROSE edit_with_preview error',
          },
        },
      };
    } else {
      const rendered = response.renderedQuestionnaire;
      const output = rendered?.renderOutcome;

      if (output?.output?.$case !== 'questionnaire') {
        throw Error(
          '[Assert failed] Unable to process PROSE edit_with_preview response, questionnaire is not defined in structure.'
        );
      }

      const questionnaire = getDisplayStructure(output.output.value);

      if (!rendered?.context) {
        throw Error(
          '[Assert failed] PROSE edit_with_preview returned no context. This indicates a bug in PROSE.'
        );
      }

      const fieldsData = rendered.fieldData ?? FieldDataExt.new();
      const contextData = rendered.context;
      const calculationsData = rendered.calculationsData;

      const fieldsDefinitions = getFieldDefinitions(questionnaire);

      proseOutput = {
        i18nBaseKey: questionnaire.i18nBaseKey,
        i18nKeys: {
          i18nHeaderKey: questionnaire.i18nHeaderKey,
          i18nDescriptionKey: questionnaire.i18nDescriptionKey,
          i18nFooterKey: questionnaire.i18nFooterKey,
        },
        presentation: questionnaire.presentation,
        fieldsDefinitions,
        fieldsData,
        fieldsSections: questionnaire.sections,
        contextData,
        calculationsData,
      };
    }

    logger.debug('PROSE protocolEditorService.edit_with_preview output', {
      details: proseOutput,
    });

    return proseOutput;
  } finally {
    addPerformanceEndMark('proseEditWithPreviewResponseProcessing', {
      console: true,
    });
  }
};

export const callProseStartEditWithPreview = async (
  input: ProseStartEditorRenderInput
): Promise<ProseEditorRenderOutput> => {
  throwIfPanicked();
  logger.debug('PROSE protocolEditorService.start_edit_with_preview input', {
    details: input,
  });

  let response: StartEditWithPreviewResponse;

  try {
    addPerformanceStartMark('proseStartEditWithPreviewWasmCall');
    const protocolEditorService = await getProtocolEditorService();
    const initialContext = InitialRenderContextExt.new(
      LocalTimeExt.now(input.timezone)
    );
    response = protocolEditorService.start_edit_with_preview({
      protocolSource: protocolSourceOf(input.dslSource),
      questionnaireName: input.questionnaireName,
      context: initialContext,
    });
  } catch (error) {
    logger.debug(
      'PROSE protocolEditorService.start_edit_with_preview error (used for editor)',
      {
        details:
          error instanceof Error
            ? { message: error.message }
            : { error: String(error) },
      }
    );

    throw error;
  } finally {
    addPerformanceEndMark('proseStartEditWithPreviewWasmCall', {
      console: true,
    });
  }

  try {
    addPerformanceStartMark('proseStartEditWithPreviewResponseProcessing');

    let proseOutput: ProseEditorRenderOutput;

    if (response.error.length > 0) {
      const firstError = response.error[0];

      proseOutput = {
        i18nBaseKey: '',
        i18nKeys: {
          i18nHeaderKey: '',
          i18nDescriptionKey: '',
          i18nFooterKey: '',
        },
        presentation: undefined,
        fieldsDefinitions: [],
        fieldsSections: [],
        fieldsData: FieldDataExt.new(),
        calculationsData: CalculationsDataExt.new(),
        contextData: null,
        renderError: firstError ?? {
          i18nKey: 'errors.prose.render_failed',
          values: {
            message: 'Unknown PROSE start_edit_with_preview error',
          },
        },
      };
    } else {
      const rendered = response.renderedQuestionnaire;
      const output = rendered?.renderOutcome;

      if (output?.output?.$case !== 'questionnaire') {
        throw Error(
          '[Assert failed] Unable to process PROSE start_edit_with_preview response, questionnaire is not defined in structure.'
        );
      }

      const questionnaire = getDisplayStructure(output.output.value);

      if (!rendered?.context) {
        throw Error(
          '[Assert failed] PROSE start_edit_with_preview returned no context. This indicates a bug in PROSE.'
        );
      }

      const fieldsData = rendered.fieldData ?? FieldDataExt.new();
      const contextData = rendered.context;
      const calculationsData = rendered.calculationsData;

      const fieldsDefinitions = getFieldDefinitions(questionnaire);

      proseOutput = {
        i18nBaseKey: questionnaire.i18nBaseKey,
        i18nKeys: {
          i18nHeaderKey: questionnaire.i18nHeaderKey,
          i18nDescriptionKey: questionnaire.i18nDescriptionKey,
          i18nFooterKey: questionnaire.i18nFooterKey,
        },
        presentation: questionnaire.presentation,
        fieldsDefinitions,
        fieldsData,
        fieldsSections: questionnaire.sections,
        contextData,
        calculationsData,
      };
    }

    logger.debug('PROSE protocolEditorService.start_edit_with_preview output', {
      details: proseOutput,
    });

    return proseOutput;
  } finally {
    addPerformanceEndMark('proseStartEditWithPreviewResponseProcessing', {
      console: true,
    });
  }
};

// `ParticipantState` is an opaque, PROSE-managed envelope; it can only be
// obtained from `create_participant_state` and evolved through
// `record_external_event_firing`. This builds a state carrying the given
// external-event firings for schedule evaluation.
export const callProseBuildParticipantState = async (
  dslSource: string,
  firings: ExternalEventFiring[]
): Promise<ParticipantState> => {
  throwIfPanicked();

  try {
    const protocolEditorService = await getProtocolEditorService();
    const protocolSource = protocolSourceOf(dslSource);
    let state = createParticipantState(protocolEditorService);
    for (const firing of firings) {
      const { state: nextState } =
        protocolEditorService.record_external_event_firing({
          protocolSource,
          state,
          firing,
        });
      if (!nextState) {
        throw Error(
          '[Assert failed] PROSE record_external_event_firing returned no state. This indicates a bug in PROSE.'
        );
      }
      state = nextState;
    }
    return state;
  } catch (error) {
    throw new ApplicationError(
      'PHW-P-001',
      error instanceof Error
        ? error.message
        : 'Unknown PROSE build participant state error'
    );
  }
};

export const callProseParticipantSchedule = async (
  dslSource: string,
  scheduleName: string,
  query: ParticipantScheduleRequestQuery,
  callMode: 'normal' | 'for-editor' = 'normal'
): Promise<ParticipantScheduleResponse | string> => {
  throwIfPanicked();
  let response: ParticipantScheduleResponse;

  try {
    addPerformanceStartMark('proseScheduleWasmCall');
    const protocolEditorService = await getProtocolEditorService();
    response = protocolEditorService.participant_schedule({
      protocolSource: protocolSourceOf(dslSource),
      schedule: scheduleName,
      query,
    });
  } catch (error) {
    // Unlike render, participant_schedule throws an error instead of returning a value that describes the issue
    if (callMode === 'normal') {
      logger.error(
        'prose.participant_schedule error',
        undefined,
        error as Error
      );
    } else {
      logger.debug(
        'prose.participant_schedule error (used for editor)',
        undefined,
        error as Error
      );
    }
    return (error as Error).message || 'Unable to obtain participant schedule';
  } finally {
    addPerformanceEndMark('proseScheduleWasmCall', { console: true });
  }

  return response;
};

export const callProseGetScheduleName = async (
  dslSource: string
): Promise<string> => {
  throwIfPanicked();
  const protocolEditorService = await getProtocolEditorService();
  return resolveScheduleName(
    protocolEditorService.get_schedules({
      protocolSource: protocolSourceOf(dslSource),
    }).scheduleNames
  );
};

export const callDebugInfo = async () => {
  throwIfPanicked();
  const protocolEditorService = await getProtocolEditorService();
  return protocolEditorService.debuginfo();
};
